import { IconFileWithItemsOutline, IconSend } from '@iwoca/icons';
import { GetApiIntroducerPortalIntroducerApplicationDocumentFeedbackResponse } from '@iwoca/lapi-client/introducer_portal';
import { Accordion, Button, Pill, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useEmail } from 'hooks/useEmail';

import { useMarkFeedbackAsDone } from 'lendingApi/lendingApiHooks';
import groupStyles from '../DocumentGroup/DocumentGroup.module.css';
import { getAdditionalInfoMessage } from './AdditionalInfoGroup.helper';
import styles from './AdditionalInfoGroup.module.css';

type TProps = {
  additionalInfoRequests: GetApiIntroducerPortalIntroducerApplicationDocumentFeedbackResponse['data']['additional_info_requests'];
  isRefetchingDocumentFeedback?: boolean;
};

export const AdditionalInfoGroup = ({
  additionalInfoRequests,
  isRefetchingDocumentFeedback,
}: TProps) => {
  const { introducerCompany } = useIntroducerData();
  const companyName =
    introducerCompany?.public_facing_company_name ||
    introducerCompany?.company_name;
  const { email: supportEmail } = useEmail(false, companyName);

  const allMarkedAsDone =
    additionalInfoRequests?.every((request) => request.marked_as_done) ?? false;

  const { isPending, mutate: markAsDone } = useMarkFeedbackAsDone();

  const handleMarkAsDone = () => {
    const additionalInfoIds = additionalInfoRequests
      .filter((request) => !request.marked_as_done)
      .map((request) => request.id);

    markAsDone([{ type: 'additional_info', ids: additionalInfoIds }]);
  };

  return (
    <Accordion
      className={groupStyles.accordion}
      titleClassName={groupStyles.titleContainer}
      title={
        <>
          <p className={groupStyles.title}>{t`Missing information`}</p>
          <Pill
            className={groupStyles.pill}
            variant={allMarkedAsDone ? 'london' : 'red'}
          >
            {isRefetchingDocumentFeedback || isPending ? (
              <Spinner className={groupStyles.spinner} size="small" />
            ) : allMarkedAsDone ? (
              t`Processing`
            ) : (
              t`Information requested`
            )}
          </Pill>
        </>
      }
    >
      <div className={styles.body}>
        <p className={styles.intro}>
          <Trans>
            Please send us the following information by email to{' '}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
          </Trans>
        </p>
        <ul className={styles.itemList}>
          {additionalInfoRequests
            .map((request) => ({
              request,
              message: getAdditionalInfoMessage(request.request_type),
            }))
            .filter(({ message }) => message)
            .map(({ request, message }) => (
              <li key={request.id} className={styles.item}>
                <IconFileWithItemsOutline
                  width={20}
                  height={20}
                  className={styles.itemIcon}
                />
                <div>
                  <p className={styles.itemName}>{message?.label}</p>
                  <p className={styles.itemDescription}>
                    {message?.description}
                  </p>
                </div>
              </li>
            ))}
        </ul>
        <div className={styles.buttonContainer}>
          <Button
            variant={allMarkedAsDone ? 'secondary-alt' : 'secondary'}
            size="small"
            onClick={handleMarkAsDone}
            loading={isPending}
            disabled={allMarkedAsDone || isPending}
          >
            <Trans>Mark as done</Trans>
            <IconSend width={13} height={13} />
          </Button>
        </div>
      </div>
    </Accordion>
  );
};
